// ============================================================
// ENTERPRISE ROUTE — Fase 13.5
// Protege rotas enterprise (/twin, /vision, /executive)
// Mapeia company_role do AuthContext → UserRole do routeGuard
// ============================================================
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { canAccessRoute } from '@/core/routeGuard'
import type { UserRole } from '@/core/routeGuard'
import type { ReactNode } from 'react'

interface EnterpriseRouteProps {
  children: ReactNode
}

// Map AuthContext company_role → routeGuard UserRole['role']
// The hierarchy aligns with the existing ROLE_GRANTS in routeGuard.ts
const ROLE_MAP: Record<string, UserRole['role']> = {
  viewer:    'viewer',
  engineer:  'operator',   // engineers = operators in enterprise
  admin:     'manager',    // company admins get manager-level enterprise access
  owner:     'executive',  // owners get full executive access
}

export default function EnterpriseRoute({ children }: EnterpriseRouteProps) {
  const { user, loading } = useAuth()
  const { pathname }      = useLocation()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-5 h-5 border-2 rounded-full animate-spin"
          style={{ borderColor: 'hsl(var(--amber))', borderTopColor: 'transparent' }}
        />
      </div>
    )
  }

  if (!user) return <Navigate to="/" replace />

  const mappedRole = ROLE_MAP[user.company_role ?? 'viewer'] ?? 'viewer'

  const userRole: UserRole = {
    role:       mappedRole,
    company_id: user.company_id ?? '',
    obra_ids:   [],   // enforcement is at the page level (obraAtiva scoping)
  }

  if (!canAccessRoute(userRole, pathname)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
